import type { ExamItemAnswerAiCorrectionViewModel } from '@/shared/api/generated/types';

/**
 * Extrai do resultado da IA o texto que vai para o comentário da correção.
 *
 * Apenas a justificativa é aproveitada: nota, critérios, pontos fortes e pontos a
 * melhorar já aparecem no painel de resultado da análise, e repeti-los no comentário
 * enviado ao aluno enchia o campo de dados de auditoria interna.
 *
 * @param result - Resultado do endpoint de correção por IA
 * @returns Justificativa sem espaços nas pontas, ou string vazia se não houver
 *
 * @example
 * ```ts
 * const block = buildAiCorrectionComment(aiResult);
 * ```
 */
export function buildAiCorrectionComment(result: ExamItemAnswerAiCorrectionViewModel): string {
  return result.justification?.trim() ?? '';
}

/**
 * Mescla o texto gerado pela IA ao comentário atual do corretor.
 *
 * Preserva o texto escrito manualmente e substitui o bloco aplicado anteriormente,
 * evitando duplicação quando o corretor reanalisa e aplica novamente. Como o texto
 * não tem marcadores, a identificação do bloco anterior vem de `previousBlock` —
 * quem chama guarda o último bloco aplicado. Se ele não estiver mais presente
 * (o corretor editou ou apagou o trecho), o bloco novo é apenas anexado.
 *
 * @param current - Comentário atual do campo (pode conter texto manual e/ou um bloco anterior)
 * @param block - Texto devolvido por {@link buildAiCorrectionComment}
 * @param previousBlock - Bloco aplicado na vez anterior, se houver
 * @returns Comentário resultante, com o texto da IA no fim
 *
 * @example
 * ```ts
 * setComment(current => mergeAiCorrectionComment(current, block, appliedBlockRef.current));
 * ```
 */
export function mergeAiCorrectionComment(
  current: string,
  block: string,
  previousBlock?: string,
): string {
  if (!block) return current;

  const manualText =
    previousBlock && current.includes(previousBlock)
      ? current.replace(previousBlock, '').trim()
      : current.trim();

  return manualText ? `${manualText}\n\n${block}` : block;
}
